import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR');
}

function formatMontant(val) {
  if (!val && val !== 0) return '—';
  const n = parseFloat(String(val).replace(',', '.'));
  if (isNaN(n)) return val;
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

const ETATS = ['Non répondu', 'En cours', 'Perdu', 'Gagné', 'Terminé', 'Declaration sans suite'];
const CONDITIONS = ['Non', 'Actualisable', 'Révisable', 'Actualisable et Révisable'];

const ETAT_BADGE = {
  'Non répondu': { background: '#e2e3e5', color: '#383d41' },
  'En cours':    { background: '#d1ecf1', color: '#0c5460' },
  'Perdu':       { background: '#f8d7da', color: '#721c24' },
  'Gagné':       { background: '#d4edda', color: '#155724' },
  'Terminé':     { background: '#e2d5f1', color: '#4a235a' },
  'Declaration sans suite': { background: '#fde9d0', color: '#9c4221' },
};

// ─── Composant Champ Éditable ────────────────────────────────────────────────
function EditableField({ label, value, field, disabled, onSave, options, type }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');

  useEffect(() => { setVal(value || ''); }, [value]);

  const handleSave = async () => {
    try {
      await onSave(field, val);
      setEditing(false);
    } catch (err) {
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleCancel = () => { setVal(value || ''); setEditing(false); };

  const inputStyle = {
    padding: '0.5rem 0.7rem', border: '2px solid #3498db',
    borderRadius: '4px', fontSize: '0.9rem', flex: 1, outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div className="detail-item">
      <div className="detail-label">{label}</div>
      <div className="detail-value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {editing ? (
          <>
            {options ? (
              <select value={val} onChange={e => setVal(e.target.value)}
                style={{ ...inputStyle, background: '#fff' }} autoFocus
                onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') handleCancel(); }}>
                <option value="">— Sélectionner —</option>
                {options.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input type={type || 'text'} value={val} onChange={e => setVal(e.target.value)}
                style={inputStyle} autoFocus
                onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') handleCancel(); }} />
            )}
            <button onClick={handleSave} title="Enregistrer"
              style={{ background: '#27ae60', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.4rem 0.7rem', cursor: 'pointer', fontWeight: 600 }}>✓</button>
            <button onClick={handleCancel} title="Annuler"
              style={{ background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.4rem 0.7rem', cursor: 'pointer', fontWeight: 600 }}>✕</button>
          </>
        ) : (
          <>
            <span style={{ flex: 1 }}>
              {field === 'etat' && value ? (
                <span style={{
                  ...(ETAT_BADGE[value] || ETAT_BADGE['En cours']),
                  padding: '0.25rem 0.6rem', borderRadius: '12px',
                  fontSize: '0.82rem', fontWeight: 600,
                }}>{value}</span>
              ) : field === 'montant' && value ? (
                formatMontant(value)
              ) : field === 'dlro' || field === 'notification' ? (
                value ? fmtDate(value) : '—'
              ) : (
                value || '—'
              )}
            </span>
            {!disabled && (
              <button onClick={() => setEditing(true)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.2rem 0.4rem', opacity: 0.4, display: 'flex', alignItems: 'center', transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'scale(1.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '0.4'; e.currentTarget.style.transform = 'scale(1)'; }}
                title="Modifier ce champ">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#95a5a6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────
function MarcheDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [marche, setMarche] = useState(null);
  const [dc4s, setDc4s] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchMarche(); }, [id]); // eslint-disable-line

  const fetchMarche = async () => {
    try {
      const r = await axios.get('/api/marches/' + id);
      setMarche(r.data);
      // Charger les DC4 associés via route dédiée
      try {
        const dc4Res = await axios.get('/api/dc4/by-marche/' + id);
        setDc4s(dc4Res.data);
      } catch (e) { console.error('Erreur chargement DC4:', e); }
    } catch (e) { console.error('Erreur chargement marché:', e); }
    finally { setLoading(false); }
  };

  const handleFieldSave = async (field, value) => {
    try {
      // Construire le payload complet pour le PUT
      const payload = { ...marche, [field]: value || null };
      // Nettoyer les champs calculés
      delete payload.client_nom;
      delete payload.entreprise_nom;
      delete payload.codification;
      delete payload.reference_interne;
      delete payload.created_at;
      delete payload.updated_at;

      await axios.put('/api/marches/' + id, payload);
      await fetchMarche();
    } catch (err) {
      throw new Error(err.response?.data?.error || 'Erreur de sauvegarde');
    }
  };

  const getEtat = () => {
    if (marche.etat) return marche.etat;
    if (marche.retenu) return 'Gagné';
    return 'En cours';
  };

  if (loading) return <div className="loading">Chargement…</div>;
  if (!marche) return <div className="error-message">Marché non trouvé</div>;

  const etat = getEtat();

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button onClick={() => navigate('/marches')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#7f8c8d' }}
              title="Retour à la liste">
              ←
            </button>
            <div>
              <h1 className="card-title" style={{ marginBottom: '0.25rem' }}>
                {marche.codification || 'Marché #' + marche.reference_interne}
              </h1>
              <div style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>
                {marche.intitule_marche}
              </div>
            </div>
          </div>
          <span style={{
            ...(ETAT_BADGE[etat] || ETAT_BADGE['En cours']),
            padding: '0.4rem 1rem', borderRadius: '12px',
            fontSize: '0.9rem', fontWeight: 600,
          }}>
            {etat}
          </span>
        </div>

        {/* Informations générales */}
        <div className="detail-section">
          <h3>Informations générales</h3>
          <div className="detail-grid">
            <EditableField label="Codification" value={marche.codification} field="codification" disabled onSave={handleFieldSave} />
            <EditableField label="Intitulé" value={marche.intitule_marche} field="intitule_marche" onSave={handleFieldSave} />
            <EditableField label="Client" value={marche.client_nom} field="client_nom" disabled onSave={handleFieldSave} />
            <EditableField label="Référence client" value={marche.reference_client} field="reference_client" onSave={handleFieldSave} />
            <EditableField label="Lot" value={marche.lot} field="lot" onSave={handleFieldSave} />
            {user?.role === 'Admin' && (
              <EditableField label="Entreprise" value={marche.entreprise_nom} field="entreprise_nom" disabled onSave={handleFieldSave} />
            )}
          </div>
        </div>

        {/* Financier */}
        <div className="detail-section">
          <h3>Informations financières</h3>
          <div className="detail-grid">
            <EditableField label="Montant" value={marche.montant} field="montant" onSave={handleFieldSave} />
            <EditableField label="Modalité Évolution Prix" value={marche.conditions_financieres} field="conditions_financieres" onSave={handleFieldSave} options={CONDITIONS} />
          </div>
        </div>

        {/* Calendrier & État */}
        <div className="detail-section">
          <h3>Calendrier & État</h3>
          <div className="detail-grid">
            <EditableField label="DLRO" value={marche.dlro ? new Date(marche.dlro).toISOString().split('T')[0] : ''} field="dlro" onSave={handleFieldSave} type="date" />
            <EditableField label="Date de notification" value={marche.notification ? new Date(marche.notification).toISOString().split('T')[0] : ''} field="notification" onSave={handleFieldSave} type="date" />
            <EditableField label="État" value={etat} field="etat" onSave={handleFieldSave} options={ETATS} />
          </div>
        </div>

        {/* DC4 associés */}
        <div className="detail-section">
          <h3>DC4 associés ({dc4s.length})</h3>
          {dc4s.length === 0 ? (
            <p style={{ color: '#95a5a6' }}>Aucun DC4 associé à ce marché</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Numéro DC4</th>
                  <th>Sous-traitant</th>
                  <th>Nature des prestations</th>
                  <th>Montant prévisionnel</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {dc4s.map(d => (
                  <tr key={d.id}>
                    <td><strong>{d.numero_dc4 || '#' + d.id}</strong></td>
                    <td>{d.soustraitant_nom || '—'}</td>
                    <td style={{ maxWidth: '200px' }}>{d.nature_prestations || '—'}</td>
                    <td style={{ textAlign: 'right' }}>{d.montant_previsionnel ? formatMontant(d.montant_previsionnel) : '—'}</td>
                    <td><span className="badge badge-info">{d.statut}</span></td>
                    <td>
                      <button onClick={() => navigate('/dc4/' + d.id)} className="btn btn-primary"
                        style={{ padding: '0.35rem 0.6rem', minWidth: '32px' }} title="Détail">D</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default MarcheDetail;
